import { Activity } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../Tooltip/Tooltip"

const PILL_BASE =
  "inline-flex items-center justify-center px-1.5 py-0.5 rounded text-caption bg-muted border border-border text-foreground tabular-nums min-w-[64px]"

interface MultiValueTriggerProps {
  triggerClassName: string
  label: string
  ariaLabel: string
  headerText: string
  values: string[]
}

function MultiValueTrigger({
  triggerClassName,
  label,
  ariaLabel,
  headerText,
  values,
}: MultiValueTriggerProps) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className={triggerClassName} aria-label={ariaLabel}>
            {label}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[220px] p-0 overflow-hidden">
          <div className="px-2.5 py-1 border-b border-background/15 text-caption uppercase tracking-wider text-background/70">
            {headerText}
          </div>
          <ul className="px-2.5 py-1.5 flex flex-col gap-0.5">
            {values.map((v) => (
              <li key={v} className="text-caption font-mono tabular-nums text-background">
                {v}
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export interface DeploymentSymbolPillProps {
  symbol: string
  symbols?: string[]
  comboLabel?: string
  countSuffix?: string
  plain?: boolean
  multiHeaderText?: (count: number) => string
  className?: string
}

export function DeploymentSymbolPill({
  symbol,
  symbols,
  comboLabel = "COMBO",
  countSuffix = "",
  plain = false,
  multiHeaderText,
  className,
}: DeploymentSymbolPillProps) {
  const isCombo = symbol === comboLabel && Array.isArray(symbols) && symbols.length > 0

  if (!isCombo) {
    return <span className={cn(PILL_BASE, className)}>{symbol}</span>
  }

  const label = `+${symbols.length}${countSuffix}`
  if (plain) {
    return <span className={cn(PILL_BASE, className)}>{label}</span>
  }

  const headerText = multiHeaderText
    ? multiHeaderText(symbols.length)
    : `Backtested on ${symbols.length} pairs`

  return (
    <MultiValueTrigger
      triggerClassName={cn(
        PILL_BASE,
        "cursor-help hover:bg-muted/70 transition-colors underline decoration-dotted decoration-muted-foreground/50 underline-offset-2",
        className,
      )}
      label={label}
      ariaLabel={`${symbols.length} symbols: ${symbols.join(", ")}`}
      headerText={headerText}
      values={symbols}
    />
  )
}

export interface DeploymentTimeframePillProps {
  timeframe: string
  timeframes?: string[]
  multiLabel?: string
  countSuffix?: string
  plain?: boolean
  multiHeaderText?: (count: number) => string
  className?: string
}

export function DeploymentTimeframePill({
  timeframe,
  timeframes,
  multiLabel = "Multi",
  countSuffix = "",
  plain = false,
  multiHeaderText,
  className,
}: DeploymentTimeframePillProps) {
  const isMulti =
    timeframe === multiLabel && Array.isArray(timeframes) && timeframes.length > 0

  if (!isMulti) {
    return (
      <span className={cn("text-caption text-foreground tabular-nums", className)}>
        {timeframe}
      </span>
    )
  }

  const label = `+${timeframes.length}${countSuffix}`
  if (plain) {
    return (
      <span className={cn("text-caption text-foreground tabular-nums", className)}>
        {label}
      </span>
    )
  }

  const headerText = multiHeaderText
    ? multiHeaderText(timeframes.length)
    : `Backtested on ${timeframes.length} timeframes`

  return (
    <MultiValueTrigger
      triggerClassName={cn(
        "inline-flex items-center text-caption text-foreground tabular-nums cursor-help underline decoration-dotted decoration-muted-foreground/50 underline-offset-2 hover:text-foreground",
        className,
      )}
      label={label}
      ariaLabel={`${timeframes.length} timeframes: ${timeframes.join(", ")}`}
      headerText={headerText}
      values={timeframes}
    />
  )
}

export interface DeploymentStatusBadgeProps {
  running: boolean
  runningLabel?: string
  offlineLabel?: string
  className?: string
}

export function DeploymentStatusBadge({
  running,
  runningLabel = "Running",
  offlineLabel = "Offline",
  className,
}: DeploymentStatusBadgeProps) {
  if (running) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 text-caption font-medium text-emerald-600 dark:text-emerald-400",
          className,
        )}
      >
        <Activity size={12} strokeWidth={2.25} className="text-emerald-500 animate-pulse" />
        {runningLabel}
      </span>
    )
  }
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-caption text-muted-foreground",
        className,
      )}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50" />
      {offlineLabel}
    </span>
  )
}
